import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, lazy, Suspense } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrophyIcon, TrendingUpIcon, UsersIcon, SearchIcon } from 'lucide-react';
import { PerformanceCard, RoleBadge } from '@/components/employees';

// Lazy load chart component
const PerformanceChart = lazy(() =>
  import('@/components/employees/PerformanceChart').then(module => ({ default: module.PerformanceChart }))
);
import { useTeams } from '@/lib/hooks/useTeams';
import { useEmployees } from '@/lib/hooks/useEmployees';
import type { DateRange } from 'react-day-picker';

export const Route = createFileRoute('/admin/performance')({
  component: PerformancePage,
});

function PerformancePage() {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 90)), // Last 90 days
    to: new Date(),
  });
  const [leaderboardSearch, setLeaderboardSearch] = useState('');

  // Get team and employee data from real API
  const { data: teamsData } = useTeams(1, 100);
  const allTeams = teamsData?.data ?? [];
  const { data: allEmployeesData } = useEmployees({ status: 'active' }, 1, 100);
  const allEmployees = allEmployeesData?.data ?? [];

  const selectedTeam = selectedTeamId !== 'all' ? allTeams.find((t) => t.id === selectedTeamId) ?? null : null;
  const teamEmployees = selectedTeamId !== 'all'
    ? allEmployees.filter((e:any) => e.teamId === selectedTeamId)
    : allEmployees;

  // Calculate team metrics
  const teamMetrics = useMemo(() => {
    const totalJobs = teamEmployees.reduce((sum:any, emp:any) => sum + emp.metrics.totalJobsPosted, 0);
    const avgQuality = teamEmployees.reduce((sum:any, emp:any) => sum + emp.metrics.avgQualityScore, 0) / teamEmployees.length || 0;
    const avgFeedback = teamEmployees.reduce((sum:any, emp:any) => sum + emp.metrics.customerFeedback, 0) / teamEmployees.length || 0;
    const avgResponseTime = teamEmployees.reduce((sum:any, emp:any) => sum + emp.metrics.responseTime, 0) / teamEmployees.length || 0;

    // Sort by quality score for top performers
    const sortedByQuality = [...teamEmployees].sort((a, b) => b.metrics.avgQualityScore - a.metrics.avgQualityScore);
    const topPerformers = sortedByQuality.slice(0, 5);

    return {
      totalJobs,
      avgQuality: Math.round(avgQuality),
      avgFeedback: avgFeedback.toFixed(1),
      avgResponseTime: avgResponseTime.toFixed(1),
      topPerformers,
      memberCount: teamEmployees.length,
    };
  }, [teamEmployees]);

  // No performance trends API yet — chart will show empty state
  const aggregatedTrends = useMemo(() => [], []);

  // Leaderboard
  const leaderboard = useMemo(() => {
    return [...teamEmployees]
      .sort((a, b) => b.metrics.avgQualityScore - a.metrics.avgQualityScore)
      .map((emp, index) => ({
        ...emp,
        rank: index + 1,
      }));
  }, [teamEmployees]);

  // Filtered leaderboard for table (positions 6+)
  const filteredLeaderboard = useMemo(() => {
    if (!leaderboardSearch) return leaderboard.slice(5);

    const searchLower = leaderboardSearch.toLowerCase();
    return leaderboard.slice(5).filter((employee) => {
      const matchesName = employee.name.toLowerCase().includes(searchLower);
      const matchesRole = employee.role.toLowerCase().includes(searchLower);
      return matchesName || matchesRole;
    });
  }, [leaderboard, leaderboardSearch]);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header with Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team Performance</h1>
          <p className="text-muted-foreground">
            {selectedTeam ? `${selectedTeam.name} analytics` : 'All teams aggregate performance'}
          </p>
        </div>

        {/* Filters on the right */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <DateRangePicker date={dateRange} onDateChange={setDateRange} />
          <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Select Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {allTeams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Team Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <PerformanceCard
          title="Total Jobs Posted"
          value={teamMetrics.totalJobs}
          change={8.2}
          trend="up"
          description="from last period"
          icon={TrendingUpIcon}
        />
        <PerformanceCard
          title="Avg Quality Score"
          value={`${teamMetrics.avgQuality}%`}
          change={3.1}
          trend="up"
          description="team average"
        />
        <PerformanceCard
          title="Team Members"
          value={teamMetrics.memberCount}
          description="active employees"
          icon={UsersIcon}
        />
        <PerformanceCard
          title="Avg Feedback"
          value={teamMetrics.avgFeedback}
          change={0.4}
          trend="up"
          description="out of 5.0"
        />
      </div>

      {/* Performance Chart - Lazy loaded */}
      <Suspense fallback={<Card><CardContent className="h-[400px] flex items-center justify-center text-sm text-muted-foreground">Loading chart...</CardContent></Card>}>
        <PerformanceChart
          data={aggregatedTrends}
          title="Team Performance Trends"
          description="Aggregated team performance over 12 weeks"
          showJobsPosted={true}
          showQualityScore={true}
        />
      </Suspense>

      {/* Top Performers Grid */}
      <div>
        {/* Grid: Top 3 full cards, then 4th & 5th in one card */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Top 3 Performers - Full Cards */}
          {teamMetrics.topPerformers.slice(0, 3).map((employee, index) => {
            const initials = employee.name
              .split(' ')
              .map((n:any) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2);

            const medalColors = ['text-yellow-600', 'text-gray-400', 'text-orange-600'];
            const bgColors = ['bg-yellow-50 dark:bg-yellow-950', 'bg-gray-50 dark:bg-gray-900', 'bg-orange-50 dark:bg-orange-950'];

            return (
              <Card key={employee.id} className={bgColors[index]}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-3">
                      <div className="relative">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <TrophyIcon className={`absolute -top-1 -right-1 h-5 w-5 ${medalColors[index]}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{employee.name}</h3>
                        <RoleBadge role={employee.role} className="mt-1" />
                        {employee.team && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {employee.team.name}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-lg font-bold">
                      #{index + 1}
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground mb-1">Quality</div>
                        <div className="text-2xl font-bold">{employee.metrics.avgQualityScore}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground mb-1">Jobs</div>
                        <div className="text-2xl font-bold">{employee.metrics.totalJobsPosted}</div>
                      </div>
                    </div>
                    <div className="text-center pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1">Rating</div>
                      <div className="text-xl font-bold">{employee.metrics.customerFeedback.toFixed(1)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* 4th & 5th in same card */}
          {teamMetrics.topPerformers.length >= 4 && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {teamMetrics.topPerformers.slice(3, 5).map((employee, index) => {
                    const initials = employee.name
                      .split(' ')
                      .map((n:any) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <div key={employee.id} className="space-y-3">
                        {index > 0 && <div className="border-t" />}
                        <div className="flex items-start justify-between">
                          <div className="flex gap-2 items-center">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="text-sm font-semibold">{employee.name}</h4>
                              <RoleBadge role={employee.role} className="text-xs mt-0.5" />
                            </div>
                          </div>
                          <Badge variant="outline" className="font-bold">
                            #{index + 4}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <div className="text-xs text-muted-foreground">Quality</div>
                            <div className="text-xs font-semibold">{employee.metrics.avgQualityScore}%</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Jobs</div>
                            <div className="text-xs font-semibold">{employee.metrics.totalJobsPosted}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Rating</div>
                            <div className="text-xs font-semibold">{employee.metrics.customerFeedback.toFixed(1)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Leaderboard Table for 6+ */}
      {leaderboard.length > 5 && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle>Leaderboard Table</CardTitle>
                <CardDescription>Complete rankings from position 6 onwards</CardDescription>
              </div>
              <div className="relative w-full sm:w-[280px]">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name or role..."
                  value={leaderboardSearch}
                  onChange={(e) => setLeaderboardSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Rank</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Quality</TableHead>
                    <TableHead className="text-right">Jobs Posted</TableHead>
                    <TableHead className="text-right">Rating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeaderboard.length > 0 ? (
                    filteredLeaderboard.map((employee) => {
                    const initials = employee.name
                      .split(' ')
                      .map((n:any) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <TableRow key={employee.id}>
                        <TableCell>
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted font-semibold text-sm">
                            {employee.rank}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{employee.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <RoleBadge role={employee.role} />
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {employee.metrics.avgQualityScore}%
                        </TableCell>
                        <TableCell className="text-right">
                          {employee.metrics.totalJobsPosted}
                        </TableCell>
                        <TableCell className="text-right">
                          {employee.metrics.customerFeedback.toFixed(1)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                        No employees match your search
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
